import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bell, CheckCheck, ClipboardList, CreditCard, FileText, Mail, UserPlus, Wand2, GitCompareArrows, CheckCircle2,
} from 'lucide-react';
import { getAuthHeaders } from '@/context/AuthContext';
import { useInvitations } from '@/context/InvitationContext';
import { API_BASE } from '@/config/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ICON = {
  call_sheet_confirmed: CheckCircle2, call_sheet_sent: ClipboardList, dpr_sent: FileText, days_built: Wand2,
  script_revised: GitCompareArrows, member_added: UserPlus, invited: Mail, payment_failed: CreditCard, trial_ending: CreditCard,
};
const POLL_MS = 60000;

function ago(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/**
 * The bell (M3.6). Unread count polled every minute and on focus; pending
 * invitations show as a pinned first row so this is the one place to look.
 */
export default function NotificationsBell() {
  const navigate = useNavigate();
  const { invitationCount } = useInvitations();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/notifications/unread-count`, { headers: getAuthHeaders() });
      setUnread(data.unread_count || 0);
    } catch { /* offline or not signed in — badge just stays */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/notifications?limit=30`, { headers: getAuthHeaders() });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* keep what we have */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [fetchCount]);

  useEffect(() => { if (open) fetchList(); }, [open, fetchList]);

  const markRead = async (ids) => {
    setItems((xs) => xs.map((n) => (ids.includes(n.id) ? { ...n, read_at: n.read_at || new Date().toISOString() } : n)));
    setUnread((u) => Math.max(0, u - ids.length));
    try { await axios.post(`${API_BASE}/notifications/read`, { ids }, { headers: getAuthHeaders() }); } catch { /* next poll corrects */ }
  };
  const markAll = async () => {
    setItems((xs) => xs.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    setUnread(0);
    try { await axios.post(`${API_BASE}/notifications/read`, { all: true }, { headers: getAuthHeaders() }); } catch { /* ignore */ }
  };
  const openItem = (n) => {
    if (!n.read_at) markRead([n.id]);
    setOpen(false);
    if (n.url) navigate(n.url);
  };

  const badge = unread + (invitationCount || 0);
  const hasAny = items.length > 0 || invitationCount > 0;
  const unreadItems = useMemo(() => items.filter((n) => !n.read_at).length, [items]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 w-9 p-0 flex-shrink-0 relative" aria-label={`Notifications${badge ? ` (${badge} unread)` : ''}`} data-testid="appshell-notifications">
          <Bell className="h-5 w-5" />
          {badge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-0 overflow-hidden" data-testid="notifications-panel">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold">Notifications</span>
          {unreadItems > 0 && <span className="text-[11px] text-muted-foreground">{unreadItems} unread</span>}
          {unreadItems > 0 && (
            <button type="button" onClick={markAll} className="ml-auto text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="notifications-mark-all">
              <CheckCheck className="size-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[24rem] overflow-y-auto">
          {invitationCount > 0 && (
            <button type="button" onClick={() => { setOpen(false); navigate('/invitations'); }} className="w-full text-left px-3 py-2.5 flex gap-3 hover:bg-accent/40 border-b border-border bg-primary/5" data-testid="notifications-invitations">
              <Mail className="size-4 text-primary mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{invitationCount} pending invitation{invitationCount === 1 ? '' : 's'}</div>
                <div className="text-xs text-muted-foreground">Accept or decline</div>
              </div>
            </button>
          )}
          {items.map((n) => {
            const Icon = ICON[n.kind] || Bell;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => openItem(n)}
                className={cn('w-full text-left px-3 py-2.5 flex gap-3 hover:bg-accent/40 border-b border-border last:border-b-0', !n.read_at && 'bg-primary/5')}
                data-testid={`notification-${n.id}`}
              >
                <Icon className={cn('size-4 mt-0.5 shrink-0', n.read_at ? 'text-muted-foreground' : 'text-primary')} />
                <div className="min-w-0 flex-1">
                  <div className={cn('text-sm leading-snug', !n.read_at && 'font-medium')}>{n.title}</div>
                  {n.body && <div className="text-xs text-muted-foreground truncate">{n.body}</div>}
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0 pt-0.5">{ago(n.updated_at || n.created_at)}</span>
              </button>
            );
          })}
          {!hasAny && !loading && (
            <div className="px-3 py-8 text-center text-xs text-muted-foreground">
              Nothing yet. Call sheet confirmations, sent reports and team changes show up here.
            </div>
          )}
          {loading && items.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
